import { skipToken } from '@reduxjs/toolkit/query';
import { FormSection } from '@strata/react-form-section';
import { IntakeCaseTimeline, ReferralTimeline } from '@strata/react-timeline';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as PropTypes from 'prop-types';

import { OpportunityDetails } from 'components/opportunities/OpportunityDetails';
import { OpportunityMemberDetails } from 'components/opportunities/OpportunityMemberDetails';
import { OpportunityPrescriberDetails } from 'components/opportunities/OpportunityPrescriberDetails';
import { useIntakeCaseMilestones } from 'services/intakeCaseHelpers';
import { enrichMilestones } from 'services/milestoneHelpers';
import { useScopeAssessorMemberDetails } from 'services/useScopeAssessorMemberDetails';
import {
    useGetPrescriberFaxPrefQuery,
    useGetReferralFaxStatusQuery,
    useGetReferralMilestonesQuery,
} from 'store/api/clientApi';
import { useGetOpportunityQuery } from 'store/api/opportunityCuratorApi';
import {
    useGetAllSuppliersQuery,
    useGetPayerSegmentsQuery,
    useGetPrescriberDetailsQuery,
} from 'store/api/referenceApi';

export const OpportunityDetailsOverview = ({
    referralId,
    showMemberDetails = false,
    intakeCaseMilestoneReferralFields = undefined,
}) => {
    const { showIntakeTimelineAgora } = useFlags();
    const { data: opportunityDetails } = useGetOpportunityQuery(referralId);

    const { memberDetails, willBeUnmanagedOn } = useScopeAssessorMemberDetails(
        showMemberDetails ? opportunityDetails : undefined,
        { computeWillBeUnmanagedOn: true },
    );

    const { data: prescriberDetails, isLoading: isPrescriberDetailsLoading } =
        useGetPrescriberDetailsQuery(
            { prescriberNpi: opportunityDetails?.prescriberNpi },
            { skip: !opportunityDetails?.prescriberNpi },
        );
    const { data: prescriberFaxPref } = useGetPrescriberFaxPrefQuery(
        opportunityDetails
            ? {
                  payerId: opportunityDetails.payerId,
                  segmentId: opportunityDetails.segmentId,
              }
            : skipToken,
    );
    const { data: referralFaxStatus } = useGetReferralFaxStatusQuery(
        opportunityDetails
            ? {
                  payerId: opportunityDetails.payerId,
                  referralId: opportunityDetails.referralId,
              }
            : skipToken,
    );

    const { data: opportunityMilestones, isFetching: milestonesFetching } =
        useGetReferralMilestonesQuery(
            opportunityDetails
                ? {
                      payerId: opportunityDetails.payerId,
                      referralId: opportunityDetails.referralId,
                  }
                : skipToken,
        );
    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};
    const { data: segments } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segments ?? {};
    const enrichedMilestones = enrichMilestones({
        milestones: opportunityMilestones,
        supplierMap,
        payerSegmentMap,
    });

    const {
        milestones,
        hasMilestones,
        milestonesFetching: intakeMilestonesFetching,
    } = useIntakeCaseMilestones({
        referralDetails: intakeCaseMilestoneReferralFields,
        showIntakeTimeline: showIntakeTimelineAgora,
    });

    const shouldRenderIntakeTimeline = showIntakeTimelineAgora && hasMilestones;

    return (
        <div className="sds_details-page-overview">
            <div className="sds_details-page-overview__container" style={{ flex: '2 0' }}>
                <OpportunityDetails opportunityDetails={opportunityDetails} />
                {showMemberDetails && (
                    <OpportunityMemberDetails
                        opportunityDetails={opportunityDetails}
                        memberDetails={memberDetails}
                        willBeUnmanagedOn={willBeUnmanagedOn}
                    />
                )}
                <OpportunityPrescriberDetails
                    prescriberDetails={prescriberDetails}
                    prescriberFaxPref={prescriberFaxPref}
                    referralFaxStatus={referralFaxStatus}
                    opportunityIssues={opportunityDetails?.issues}
                    isLoading={isPrescriberDetailsLoading || !opportunityDetails}
                />
            </div>
            <div className="sds_details-page-overview__container" style={{ flex: '1 0' }}>
                {shouldRenderIntakeTimeline && (
                    <FormSection
                        sectionTitle="Intake Case Timeline"
                        isLoading={intakeMilestonesFetching}
                        content={<IntakeCaseTimeline milestones={milestones} />}
                    />
                )}
                <FormSection
                    sectionTitle="Opportunity Timeline"
                    isLoading={milestonesFetching}
                    content={<ReferralTimeline referralMilestones={enrichedMilestones} />}
                />
            </div>
        </div>
    );
};

OpportunityDetailsOverview.propTypes = {
    referralId: PropTypes.string.isRequired,
    showMemberDetails: PropTypes.bool,
    intakeCaseMilestoneReferralFields: PropTypes.shape({
        segmentId: PropTypes.string,
        memberId: PropTypes.string,
        patientFirstName: PropTypes.string,
        patientDateOfBirth: PropTypes.string,
        productServiceOid: PropTypes.string,
        receivedTime: PropTypes.string,
        endDate: PropTypes.string,
    }),
};
